import React, { useEffect, useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { useUserContext } from '../../../../common/utils/context';
import { getFullMachineName } from '../../../../common/utils/machine';
import { analyseCoinMech } from '../../../../common/utils/vending-utils';
import { Button, ErrorCallout, Heading, Text, WarningCallout } from '../../../components';
import { useThemedStyles } from '../../../hooks/useThemedStyles';
import { useTheme } from '../../../theme';
import { MapLink } from '../../map/MapLink';
import {
	DocketCoinMechDetail,
	DocketLocationDetail,
	DocketPOSDetail,
	DocketServiceStatusDetail,
} from '../../docket/docketDetails';
import FreshFoodLines from './FreshFoodLines';

const OnTheRunSection = ({
	activeDocket,
	freshFoodStop,
	onOpenPickList,
	upcomingDocket,
	sortedDockets,
	isDocketDone,
	perStopSteps,
	canCreateTicket,
	currentStopDetailStyles,
	onStepPress,
	onOpenStopDetails,
	onOpenQuickPhoto,
	onOpenCreateTicket,
	onOpenTickets,
	onGoToNextStop,
	onOpenFullRouteMap,
	onSkipPickStep,
	onSkipRestockStep,
	onOpenVisitDifferentStop,
}) => {
	const styles = useThemedStyles(createStyles);
	const { theme } = useTheme();
	const { tenantSettings } = useUserContext();
	const [showStopDetails, setShowStopDetails] = useState(false);

	// Every stop starts with the full details tucked away again.
	useEffect(() => {
		setShowStopDetails(false);
	}, [activeDocket?.id]);

	const completedStopsCount = (sortedDockets || []).filter(isDocketDone).length;
	const totalStopsCount = (sortedDockets || []).length;

	if (!totalStopsCount) {
		return null;
	}

	if (!activeDocket) {
		return (
			<View style={styles.card}>
				<Heading heading="On the run" subHeading="All stops are complete" />
				<Text style={styles.subtleText}>{`${completedStopsCount}/${totalStopsCount} stops done.`}</Text>
			</View>
		);
	}

	const activeMachineName = getFullMachineName(activeDocket.machine, activeDocket.machine.place, activeDocket.machine.location);
	const upcomingMachineName = upcomingDocket
		? getFullMachineName(upcomingDocket.machine, upcomingDocket.machine.place, upcomingDocket.machine.location)
		: null;
	const firstIncompleteStep = (perStopSteps || []).find(step => !step.done);
	const analysedCoinMech = activeDocket.machine?.hasCoinMech && !activeDocket.machine?.isOffline
		? analyseCoinMech(
			activeDocket.machine?.coinMechDetail?.tubesDetail?.map(tube => ({ ...tube })),
			activeDocket.machine?.coinMech,
			tenantSettings,
			activeDocket.machine,
		)
		: null;

	const machineLocation = activeDocket.machine?.location;
	// Same fallback as the location details block: machines without location
	// coordinates still get a map link from their own coordinates.
	const mapLocation = machineLocation
		? {
			...machineLocation,
			coordinates: machineLocation.coordinates?.length ? machineLocation.coordinates : activeDocket.machine?.coordinates,
		}
		: (activeDocket.machine?.coordinates?.length ? { coordinates: activeDocket.machine.coordinates } : null);

	const importantNotes = [
		...(machineLocation?.importantNotes || []),
		...(activeDocket.machine?.importantNotes || []),
	];

	return (
		<View style={styles.card}>
			<View style={styles.headerRow}>
				<View style={styles.headerHeadingWrap}>
					<Heading heading="Current stop" subHeading={activeMachineName || 'Unknown machine'} />
				</View>
				<Text style={styles.stopCounter}>{`${completedStopsCount}/${totalStopsCount} stops done`}</Text>
			</View>
			{
				importantNotes.map(note => (
					<ErrorCallout key={note.id} text={note.description} />
				))
			}
			{
				Boolean(freshFoodStop) && (
					<TouchableOpacity
						style={styles.freshFoodCard}
						activeOpacity={0.86}
						disabled={!activeDocket.pickList?.id}
						onPress={() => onOpenPickList(activeDocket)}
					>
						<View style={styles.freshFoodHeader}>
							<Text style={styles.freshFoodTitle}>Fresh food</Text>
							{
								Boolean(activeDocket.pickList?.id) && (
									<Ionicons name="chevron-forward" size={14} color={theme.text2} />
								)
							}
						</View>
						<FreshFoodLines stop={freshFoodStop} />
					</TouchableOpacity>
				)
			}
			{
				analysedCoinMech?.mechRequiresFill && (
					<WarningCallout text={`Coins required for this stop. ${analysedCoinMech.label}`} />
				)
			}
			{
				Boolean(mapLocation) && (
					<MapLink location={mapLocation} />
				)
			}
			{
				(perStopSteps || []).length > 0 && (
					<View style={styles.checklist}>
						{
							perStopSteps.map((step) => {
								const isNext = step.id === firstIncompleteStep?.id;

								return (
									<TouchableOpacity
										key={step.id}
										style={[
											styles.checklistRow,
											isNext && styles.checklistRowNext,
											step.blocked && styles.checklistRowBlocked,
										]}
										activeOpacity={0.86}
										disabled={step.blocked}
										onPress={() => onStepPress(step)}
									>
										<Ionicons
											name={step.done
												? 'checkmark-circle'
												: step.blocked
													? 'lock-closed-outline'
													: step.isWaiting ? 'time-outline' : 'ellipse-outline'}
											size={18}
											color={step.done ? theme.success1 : theme.text2}
										/>
										<Text style={[styles.checklistText, step.done && styles.checklistTextDone]}>{step.label}</Text>
										{
											!step.done && (
												<Ionicons name="chevron-forward" size={14} color={theme.text2} />
											)
										}
									</TouchableOpacity>
								);
							})
						}
					</View>
				)
			}
			{
				firstIncompleteStep && (
					<View style={styles.focusStepCard}>
						<Text style={styles.stepDescription}>{firstIncompleteStep.description}</Text>
						{/* Button's `secondary` prop is the solid style; reserved here for the step's main action. */}
						<Button
							label={firstIncompleteStep.blocked
								? 'Waiting for restock'
								: firstIncompleteStep.isWaiting ? 'Open restock status' : `Open ${firstIncompleteStep.label}`}
							onPress={() => onStepPress(firstIncompleteStep)}
							disabled={firstIncompleteStep.blocked}
							secondary={true}
						/>
						{
							firstIncompleteStep.id === 'restock' && firstIncompleteStep.isWaiting && !firstIncompleteStep.canSkipOffline && (
								<WarningCallout text="Waiting for restock communication. Use the restock action to check status while you continue servicing." />
							)
						}
						{
							firstIncompleteStep.id === 'restock' && firstIncompleteStep.canSkipOffline && (
								<>
									<Button
										label="Continue without waiting"
										onPress={() => onSkipRestockStep(activeDocket.id)}
										style={styles.skipStepButton}
									/>
									<Text style={styles.subtleText}>This only skips the My day step. The restock still records when connection returns.</Text>
								</>
							)
						}
						{
							firstIncompleteStep.id === 'pick' && (
								<>
									<Button
										label="Skip pick list for now"
										onPress={() => onSkipPickStep(activeDocket.id)}
										style={styles.skipStepButton}
									/>
									<Text style={styles.subtleText}>This only skips the My day step and does not change pick list quantities.</Text>
								</>
							)
						}
					</View>
				)
			}
			{
				!firstIncompleteStep && (
					<View style={styles.focusStepCard}>
						<Text style={styles.stepTitle}>Stop ready to move on</Text>
						<Text style={styles.stepDescription}>All guided tasks are complete for this stop. Move to the next stop when ready.</Text>
						{
							upcomingDocket && (
								<Button
									label="Go to next stop"
									onPress={onGoToNextStop}
									secondary={true}
								/>
							)
						}
						{
							!upcomingDocket && (
								<Button
									label="Open stop details"
									onPress={() => onOpenStopDetails(activeDocket)}
									secondary={true}
								/>
							)
						}
					</View>
				)
			}
			<View style={styles.quickActionsRow}>
				<Button
					label="Stop detail"
					onPress={() => onOpenStopDetails(activeDocket)}
					style={styles.quickActionButton}
				/>
				<Button
					label="Quick photo"
					onPress={() => onOpenQuickPhoto(activeDocket)}
					style={styles.quickActionButton}
				/>
				{
					canCreateTicket
						? (
							<Button
								label="Create ticket"
								onPress={() => onOpenCreateTicket(activeDocket)}
								style={styles.quickActionButton}
							/>
						)
						: (
							<Button
								label="Tickets"
								onPress={() => onOpenTickets(activeDocket)}
								disabled={!activeDocket?.ticketList?.id}
								style={styles.quickActionButton}
							/>
						)
				}
			</View>
			<TouchableOpacity
				style={styles.detailsToggle}
				activeOpacity={0.86}
				onPress={() => setShowStopDetails(!showStopDetails)}
			>
				<Text style={styles.detailsToggleText}>More about this stop</Text>
				<Ionicons name={showStopDetails ? 'chevron-up' : 'chevron-down'} size={16} color={theme.text2} />
			</TouchableOpacity>
			{
				showStopDetails && (
					<View style={styles.currentStopInfoCard}>
						<DocketLocationDetail docket={activeDocket} styles={currentStopDetailStyles} />
						<DocketPOSDetail docket={activeDocket} styles={currentStopDetailStyles} />
						<DocketServiceStatusDetail docket={activeDocket} styles={currentStopDetailStyles} />
						<DocketCoinMechDetail docket={activeDocket} />
					</View>
				)
			}
			{
				upcomingDocket && (
					<View style={styles.upNextCard}>
						<Text style={styles.upNextKicker}>Up next</Text>
						<Text style={styles.upNextName}>{upcomingMachineName || 'Next stop'}</Text>
						{
							upcomingDocket.machine?.location && (
								<MapLink location={upcomingDocket.machine.location} />
							)
						}
					</View>
				)
			}
			<View style={styles.footerActionsRow}>
				{
					// The parent only passes this when there is actually a stop to offer.
					Boolean(onOpenVisitDifferentStop) && (
						<Button
							label="Reorder remaining stops"
							onPress={onOpenVisitDifferentStop}
							style={styles.footerActionButton}
						/>
					)
				}
				<Button
					label="Route map"
					onPress={onOpenFullRouteMap}
					style={styles.footerActionButton}
				/>
			</View>
		</View>
	);
};

const createStyles = (theme) => ({
	card: {
		backgroundColor: theme.mainBg1,
		borderRadius: 12,
		padding: 12,
		marginBottom: 10,
		borderWidth: 1,
		borderColor: theme.border3,
		// Accent marks this as the "you are here" card among the plain sections.
		borderLeftWidth: 3,
		borderLeftColor: theme.primary1,
	},
	headerRow: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
	},
	// Long stop names must wrap instead of pushing the row past the screen edge
	// (RN rows do not shrink children by default).
	headerHeadingWrap: {
		flex: 1,
		minWidth: 0,
		paddingRight: 8,
	},
	stopCounter: {
		fontSize: 12,
		color: theme.text2,
		marginTop: 4,
		flexShrink: 0,
	},
	subtleText: {
		color: theme.text2,
		marginBottom: 8,
	},
	freshFoodCard: {
		backgroundColor: theme.mainBg3,
		borderRadius: 8,
		paddingVertical: 8,
		paddingHorizontal: 10,
		marginTop: 8,
		marginBottom: 8,
	},
	freshFoodHeader: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	freshFoodTitle: {
		fontSize: 14,
		fontWeight: '600',
		color: theme.text1,
	},
	checklist: {
		marginTop: 8,
		marginBottom: 8,
	},
	checklistRow: {
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: theme.mainBg3,
		borderRadius: 8,
		paddingVertical: 8,
		paddingHorizontal: 10,
		marginBottom: 4,
		borderWidth: 1,
		borderColor: theme.mainBg3,
	},
	checklistRowNext: {
		borderColor: theme.text1,
	},
	checklistRowBlocked: {
		opacity: 0.55,
	},
	checklistText: {
		flex: 1,
		fontSize: 14,
		fontWeight: '600',
		color: theme.text1,
		marginLeft: 8,
		paddingRight: 8,
	},
	checklistTextDone: {
		color: theme.text2,
		fontWeight: '400',
	},
	focusStepCard: {
		borderWidth: 1,
		borderColor: theme.border3,
		borderRadius: 8,
		paddingVertical: 10,
		paddingHorizontal: 10,
		marginBottom: 8,
		backgroundColor: theme.mainBg3,
	},
	stepTitle: {
		fontSize: 15,
		fontWeight: '600',
		color: theme.text1,
		marginBottom: 2,
	},
	stepDescription: {
		fontSize: 13,
		color: theme.text2,
		marginBottom: 10,
	},
	skipStepButton: {
		marginTop: 8,
	},
	quickActionsRow: {
		flexDirection: 'row',
		marginBottom: 8,
		marginHorizontal: -4,
	},
	quickActionButton: {
		flex: 1,
		marginHorizontal: 4,
	},
	detailsToggle: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingVertical: 8,
		marginBottom: 8,
		borderTopWidth: 1,
		borderTopColor: theme.border3,
	},
	detailsToggleText: {
		fontSize: 14,
		fontWeight: '600',
		color: theme.text1,
	},
	currentStopInfoCard: {
		marginBottom: 8,
		paddingBottom: 4,
	},
	upNextCard: {
		backgroundColor: theme.mainBg3,
		borderRadius: 8,
		padding: 10,
		marginBottom: 8,
	},
	upNextKicker: {
		fontSize: 11,
		fontWeight: '700',
		color: theme.text2,
		textTransform: 'uppercase',
		letterSpacing: 0.8,
		marginBottom: 2,
	},
	upNextName: {
		fontSize: 14,
		fontWeight: '600',
		color: theme.text1,
		marginBottom: 4,
	},
	footerActionsRow: {
		flexDirection: 'row',
		marginHorizontal: -4,
	},
	footerActionButton: {
		flex: 1,
		marginHorizontal: 4,
	},
});

export default OnTheRunSection;
